import {
    iMetricProtocol,
    EventsMap,
    RuntimeEventData,
    ProtocolType,
} from '../types';
import ValueHolder from './ValueHolder';
import { iPoolMember, ObjectPool } from '@/app/tools/utility_interfaces';
import EventsPrototypesHolder from '@/app/modules/Metrics/classes/EventsPrototypesHolder';

/**
 * Комбинированный тип, помечающий отправщик {@see iMetricProtocol} как член пула
 *
 * @see iPoolMember
 * @see ObjectPool
 */
export type ProtocolHandler = iMetricProtocol & iPoolMember;

/**
 * Ретранслятор информации под разными протоколами
 *
 * Ретранслятор использует пул обработчиков протоколов {@see ProtocolHandler} 1 протокол == 1 отправщик
 *
 * @see {iMetricProtocol}
 */
export default class Relay implements ObjectPool {
    /**
     * Карта обработчиков протоколов
     */
    protected readonly protocolHandlersMap: Map<
        ProtocolType,
        ProtocolHandler
    > = new Map();

    /**
     * Набор зарегистрированных событий
     */
    protected readonly events: EventsPrototypesHolder = new EventsPrototypesHolder();

    /**
     * @param newEvents
     */
    public addEvents(newEvents: EventsMap<ProtocolType>) {
        /* istanbul ignore next */
        this.events.addEvents(newEvents);
    }

    /**
     * Регистрируем протокол отправки
     *
     * @param protocolHandler
     */
    public registerProtocolHandler(protocolHandler: ProtocolHandler): void {
        const type = protocolHandler.getType();

        if (this.protocolHandlersMap.has(type)) {
            this.raiseError(`Protocol "${type}" was already registered.`);
        } else {
            this.protocolHandlersMap.set(type, protocolHandler);
        }
    }

    /**
     * @param event Number - because enums in TS may have enum
     * @param customValue
     */
    public transmit(event: string | number, customValue?: RuntimeEventData) {
        if (this.events.hasEvent(event)) {
            const eventObject = this.events.getCopy(event);

            this.sendThroughProtocols(eventObject, customValue);

            this.protocolHandlersMap.forEach(this.releaseMember, this);
        } else {
            this.raiseError(`Cannot find event "${event}".`);
        }
    }

    /**
     * Отправляем сообщение через известные протоколы
     *
     * @param eventObject
     * @param customValue
     */
    protected sendThroughProtocols(
        eventObject: Record<ProtocolType, ValueHolder | any>,
        customValue?: RuntimeEventData
    ) {
        this.protocolHandlersMap.forEach(
            (metricProtocol: ProtocolHandler, protocolType: ProtocolType) => {
                // Если у события есть данные по данному типу протокола, работаем
                /* istanbul ignore next */
                if (eventObject.hasOwnProperty(protocolType)) {
                    metricProtocol.setValue(
                        eventObject[protocolType],
                        customValue
                    );
                    metricProtocol.send(); // Отправляем
                }
            }
        );
    }

    /**
     * Поднимаем ошибку
     *
     * @param msg
     */
    protected raiseError(msg: string) {
        console.warn(`[Metrics]: ${msg}`);
    }

    /**
     * Очищаем объект пула
     *
     * @param member
     */
    protected releaseMember(member: iPoolMember) {
        member.release();
    }
}
